/**
 * Content Sheet · Public share view · Feb 2026
 *
 * Unauthenticated page the client opens via a tokened URL to review the
 * content plan. Reads `/public/content-sheet/{token}`, renders rows
 * with Approve / Reject / Comment controls per row (gated by the
 * share's allow_* flags), and posts feedback back to
 * `/public/content-sheet/{token}/feedback`.
 *
 * No auth header is sent — the token itself is the credential.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Check, X, MessageSquare, Clock, Paperclip, Link2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ContentSheetPublic() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [reviewerName, setReviewerName] = useState("");
  const [reviewerEmail, setReviewerEmail] = useState("");
  // Feb 2026 · Email-gated reviewer identity. The sheet rows stay
  // hidden behind a tiny form until the client provides a name + a
  // valid email so every approval / rejection / comment carries a
  // verified identity. We intentionally do NOT persist the identity
  // in localStorage — a share link is often forwarded between
  // reviewers at the same client, so each session must prompt fresh
  // instead of auto-filling the previous person's email.
  const [identityConfirmed, setIdentityConfirmed] = useState(false);

  const load = async () => {
    setState({ loading: true, error: null, data: null });
    try {
      const { data } = await axios.get(`${API}/api/v1/public/content-sheet/${token}`);
      setState({ loading: false, error: null, data });
      // Intentionally not auto-filling reviewer fields — a share
      // link may be forwarded internally at the client, so we always
      // ask fresh for identity.
    } catch (e) {
      const code = e?.response?.status;
      const msg = e?.response?.data?.detail || "Could not load the share";
      setState({ loading: false, error: { code, msg }, data: null });
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const isValidEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(s || "").trim());
  const canConfirmIdentity = reviewerName.trim().length >= 2 && isValidEmail(reviewerEmail);
  const confirmIdentity = () => {
    if (!canConfirmIdentity) return;
    // Feb 2026 · Fire-and-forget analytics event so PMs can see who
    // opened the share and when. Failures are silent — we never want
    // analytics to block the review flow.
    try {
      axios.post(`${API}/api/v1/public/content-sheet/${token}/opened`, {
        reviewer_name: reviewerName.trim(),
        reviewer_email: reviewerEmail.trim(),
      }).catch(() => {});
    } catch { /* noop */ }
    setIdentityConfirmed(true);
  };
  const resetIdentity = () => {
    setReviewerName("");
    setReviewerEmail("");
    setIdentityConfirmed(false);
  };

  // Resolve a human month label from the filters so the client sees
  // "Reviewing · October 2026" at the top. Must live BEFORE the
  // conditional returns to keep hook order stable.
  const monthLabel = useMemo(() => {
    const m = state.data?.filters?.month;
    if (!m || !/^\d{4}-\d{2}$/.test(m)) return null;
    try {
      const d = new Date(`${m}-01T00:00:00`);
      return d.toLocaleString("en", { month: "long", year: "numeric" });
    } catch { return m; }
  }, [state.data?.filters?.month]);

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (state.error) {
    const expired = state.error.code === 410;
    const notFound = state.error.code === 404;
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-md text-center border border-border p-8">
          <h1 className="text-xl font-semibold mb-2">
            {expired ? "This link is no longer active" : notFound ? "Share not found" : "Something went wrong"}
          </h1>
          <p className="text-sm text-muted-foreground">{state.error.msg}</p>
        </div>
      </div>
    );
  }
  const { share, workspace, rows } = state.data;

  // Feb 2026 · Email-gate — hide the sheet until the reviewer confirms
  // their identity. Keeps the share link safely forwardable without
  // losing the audit trail of exactly who approved each item.
  if (!identityConfirmed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="w-full max-w-md border border-border p-6 bg-card" data-testid="cs-public-identity-gate">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Content Plan — Review</div>
          <h1 className="text-xl font-semibold mt-1">{share.label || `${workspace.name} content plan`}</h1>
          <p className="text-xs text-muted-foreground mt-2">
            Before you see the plan, please confirm who's reviewing. Your name
            and email will be attached to every approval, rejection and comment
            so the team knows exactly who signed off.
          </p>
          <div className="mt-4 space-y-2">
            <Input value={reviewerName} onChange={(e) => setReviewerName(e.target.value)}
                   placeholder="Your full name"
                   className="h-9 rounded-none text-sm"
                   data-testid="cs-public-gate-name" />
            <Input type="email" value={reviewerEmail} onChange={(e) => setReviewerEmail(e.target.value)}
                   placeholder="you@company.com"
                   className="h-9 rounded-none text-sm"
                   data-testid="cs-public-gate-email" />
            <Button onClick={confirmIdentity} disabled={!canConfirmIdentity}
                    className="h-9 w-full rounded-none"
                    data-testid="cs-public-gate-confirm">
              Continue to content
            </Button>
            <p className="text-[10px] text-muted-foreground">
              Shared by {share.created_by_name || "the team"}
              {share.expires_at && <> · expires {share.expires_at.slice(0, 10)}</>}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const sendFeedback = async (row, action, comment = "") => {
    try {
      const { data } = await axios.post(`${API}/api/v1/public/content-sheet/${token}/feedback`, {
        content_item_id: row.id,
        action,
        comment,
        reviewer_name: reviewerName,
        reviewer_email: reviewerEmail,
      });
      toast.success(action === "approve" ? "Approved" : action === "reject" ? "Rejected" : "Comment sent");
      // Feb 2026 · Only patch the impacted card — don't re-fetch the
      // whole plan so the scroll position + typed drafts on other
      // rows are preserved.
      setState((prev) => {
        if (!prev?.data) return prev;
        const rows = prev.data.rows.map((r) =>
          r.id === row.id ? { ...r, _feedback: [...(r._feedback || []), data] } : r,
        );
        return { ...prev, data: { ...prev.data, rows } };
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record feedback");
    }
  };

  // Resolve a human month label from the filters so the client sees
  // "Reviewing · October 2026" at the top.
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-muted/30 p-3 sm:p-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Content Plan — Review</div>
          <h1 className="text-xl sm:text-2xl font-semibold mt-0.5 break-words">
            {share.brand_name || share.label || `${workspace.name} content plan`}
          </h1>
          <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-2 gap-y-0.5">
            {monthLabel && <span className="font-semibold text-foreground">{monthLabel}</span>}
            {share.project_name && <span>· {share.project_name}</span>}
            <span>· Shared by {share.created_by_name || "the team"}</span>
            {share.expires_at && <span>· expires {share.expires_at.slice(0, 10)}</span>}
          </div>
          <div className="mt-3 flex items-center gap-2 text-xs flex-wrap" data-testid="cs-public-identity-badge">
            <span className="text-muted-foreground">Reviewing as</span>
            <span className="font-semibold">{reviewerName}</span>
            <span className="text-muted-foreground hidden sm:inline">·</span>
            <span className="text-muted-foreground">{reviewerEmail}</span>
            <button type="button"
                    onClick={resetIdentity}
                    className="ml-auto sm:ml-2 text-[10px] uppercase tracking-widest text-primary hover:underline"
                    data-testid="cs-public-identity-reset">
              Not you? Switch
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto p-3 sm:p-4 space-y-3">
        {rows.length === 0 && (
          <div className="text-center text-muted-foreground italic text-sm p-10">
            Nothing to review in this plan.
          </div>
        )}
        {rows.map((r) => (
          <PublicRow key={r.id} row={r} share={share}
                      onAction={(action, comment) => sendFeedback(r, action, comment)} />
        ))}
      </main>
    </div>
  );
}

function PublicRow({ row, share, onAction }) {
  const [commentOpen, setCommentOpen] = useState(false);
  const [comment, setComment] = useState("");
  const feedback = row._feedback || [];
  // Feb 2026 · Capture the LATEST approval + LATEST rejection with
  // their timestamps so the client sees exactly when a decision
  // was recorded (and who recorded it, if the share link was
  // forwarded internally).
  const sorted = [...feedback].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
  const lastDecision = [...sorted].reverse().find((f) => f.action === "approve" || f.action === "reject");
  const lastApproval = [...sorted].reverse().find((f) => f.action === "approve");
  const lastRejection = [...sorted].reverse().find((f) => f.action === "reject");
  const comments = sorted.filter((f) => f.action === "comment");
  const atts = row.attachments || [];
  const imgs = atts.filter((a) => a.is_image);
  const files = atts.filter((a) => !a.is_image);
  const links = row.external_links || [];
  const fmtTs = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleString("en", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
    } catch { return iso.slice(0, 16).replace("T", " "); }
  };
  return (
    <div className="border border-border p-3 sm:p-4 bg-background" data-testid={`cs-public-row-${row.id}`}>
      {/* Top row: date + title + decision badge */}
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">
            {row.planned_date || "—"}
            {row.channels?.length > 0 && (
              <span className="ml-2 inline-flex gap-1">
                {row.channels.map((c) => (
                  <span key={c} className="text-[9px] font-semibold bg-muted border border-border px-1 py-0.5">{c}</span>
                ))}
              </span>
            )}
          </div>
          <div className="font-semibold text-base sm:text-lg mt-0.5 break-words">
            {row.name || "Untitled deliverable"}
          </div>
          {row.topic && (
            <div className="text-xs text-emerald-800 font-semibold mt-0.5 break-words">{row.topic}</div>
          )}
        </div>
        {lastDecision && (
          <div className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 shrink-0 ${
            lastDecision.action === "approve"
              ? "bg-emerald-50 text-emerald-800 border border-emerald-300"
              : "bg-rose-50 text-rose-800 border border-rose-300"
          }`}>
            <div>{lastDecision.action === "approve" ? "Approved" : "Rejected"} by {lastDecision.reviewer_name}</div>
            <div className="font-normal normal-case tracking-normal text-[10px] text-muted-foreground mt-0.5">
              {fmtTs(lastDecision.created_at)}
            </div>
          </div>
        )}
      </div>

      {/* Onscreen content — the headline/overlay copy that appears
          IN the creative. Shown before the caption so clients read
          what's on the asset first. */}
      {row.onscreen_content && (
        <div className="mt-3" data-testid={`cs-public-onscreen-${row.id}`}>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Onscreen content</div>
          <div className="text-sm prose prose-sm max-w-none break-words bg-amber-50/40 border border-amber-200 px-2 py-1.5"
               dangerouslySetInnerHTML={{ __html: row.onscreen_content }} />
        </div>
      )}

      {/* Onscreen content — the headline/overlay copy that appears
          IN the creative. Shown before the caption so clients read
          what's on the asset first. */}
      {row.onscreen_content && (
        <div className="mt-3" data-testid={`cs-public-onscreen-${row.id}`}>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Onscreen content</div>
          <div className="text-sm prose prose-sm max-w-none break-words bg-amber-50/40 border border-amber-200 px-2 py-1.5"
               dangerouslySetInnerHTML={{ __html: row.onscreen_content }} />
        </div>
      )}

      {/* Caption / brief */}
      {row.caption && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Caption</div>
          <div className="text-sm prose prose-sm max-w-none break-words"
               dangerouslySetInnerHTML={{ __html: row.caption }} />
        </div>
      )}

      {/* Image attachments */}
      {imgs.length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Visuals</div>
          <div className="flex flex-wrap gap-2">
            {imgs.map((a) => (
              <a key={a.id} href={a.url} target="_blank" rel="noreferrer"
                 className="block w-20 h-20 sm:w-24 sm:h-24 overflow-hidden border border-border hover:opacity-80">
                <img src={a.url} alt={a.name} className="w-full h-full object-cover" />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Non-image attachments as downloadable chips */}
      {files.length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Files</div>
          <div className="flex flex-wrap gap-1.5">
            {files.map((a) => (
              <a key={a.id} href={a.url} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1 text-xs border border-border bg-muted hover:bg-background px-2 py-1 break-all"
                 title={a.name}
                 data-testid={`cs-public-file-${a.id}`}>
                <Paperclip className="w-3 h-3 shrink-0" />
                <span className="truncate max-w-[200px]">{a.name}</span>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* External links */}
      {links.length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Links</div>
          <ul className="space-y-1">
            {links.map((l) => (
              <li key={l.id} className="text-xs">
                <a href={l.url} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1 text-primary hover:underline break-all"
                   title={l.url}
                   data-testid={`cs-public-link-${l.id}`}>
                  <Link2 className="w-3 h-3 shrink-0" />
                  <span className="break-all">{l.label || l.url}</span>
                  <ExternalLink className="w-2.5 h-2.5 opacity-60 shrink-0" />
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Comments feed */}
      {comments.length > 0 && (
        <div className="mt-3 border-t border-border pt-2">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Comments</div>
          <ul className="space-y-1.5">
            {comments.map((c) => (
              <li key={c.id} className="text-xs">
                <div className="flex flex-wrap gap-x-2 items-baseline">
                  <span className="font-semibold">{c.reviewer_name}</span>
                  <span className="text-muted-foreground">{fmtTs(c.created_at)}</span>
                </div>
                <div className="text-muted-foreground whitespace-pre-wrap break-words">{c.comment}</div>
                {Array.isArray(c.replies) && c.replies.length > 0 && (
                  <ul className="mt-1 border-l-2 border-primary/30 pl-2 space-y-1">
                    {c.replies.map((rp) => (
                      <li key={rp.id} className="text-[11px]">
                        <div className="flex flex-wrap gap-x-2 items-baseline">
                          <span className="font-semibold text-primary">{rp.author_name} (team)</span>
                          <span className="text-muted-foreground">{fmtTs(rp.created_at)}</span>
                        </div>
                        <div className="text-muted-foreground whitespace-pre-wrap break-words">{rp.text}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Approval + Rejection separate timestamps when BOTH have been
          recorded on this item — useful audit trail if the client
          flipped a decision. */}
      {lastApproval && lastRejection && (
        <div className="mt-3 flex flex-wrap gap-3 text-[10px] text-muted-foreground border-t border-border pt-2">
          <div data-testid={`cs-public-approve-ts-${row.id}`}>
            <span className="text-emerald-700 font-semibold uppercase tracking-widest">Approved</span>
            {" "}· {fmtTs(lastApproval.created_at)}
          </div>
          <div data-testid={`cs-public-reject-ts-${row.id}`}>
            <span className="text-rose-700 font-semibold uppercase tracking-widest">Rejected</span>
            {" "}· {fmtTs(lastRejection.created_at)}
          </div>
        </div>
      )}

      {/* Action buttons — mobile-friendly size, wrap to full-width row */}
      <div className="mt-3 pt-2 border-t border-border flex flex-wrap gap-1.5 justify-end">
        {share.allow_approve && (
          <Button size="sm" variant="outline"
                   onClick={() => onAction("approve")}
                   className="h-9 sm:h-7 text-xs rounded-none text-emerald-700 border-emerald-400 hover:bg-emerald-50 flex-1 sm:flex-none min-w-[90px]"
                   data-testid={`cs-public-approve-${row.id}`}>
            <Check className="w-3.5 h-3.5 mr-1" /> Approve
          </Button>
        )}
        {share.allow_reject && (
          <Button size="sm" variant="outline"
                   onClick={() => onAction("reject")}
                   className="h-9 sm:h-7 text-xs rounded-none text-rose-700 border-rose-400 hover:bg-rose-50 flex-1 sm:flex-none min-w-[90px]"
                   data-testid={`cs-public-reject-${row.id}`}>
            <X className="w-3.5 h-3.5 mr-1" /> Reject
          </Button>
        )}
        {share.allow_comment && (
          <Button size="sm" variant="outline"
                   onClick={() => setCommentOpen((o) => !o)}
                   className="h-9 sm:h-7 text-xs rounded-none flex-1 sm:flex-none min-w-[90px]"
                   data-testid={`cs-public-comment-toggle-${row.id}`}>
            <MessageSquare className="w-3.5 h-3.5 mr-1" /> Comment
          </Button>
        )}
      </div>
      {commentOpen && (
        <div className="mt-3 border-t border-border pt-2">
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)}
                     placeholder="Share your note. The team gets a bell notification."
                     rows={3} className="rounded-none text-sm"
                     data-testid={`cs-public-comment-input-${row.id}`} />
          <div className="flex justify-end mt-1 gap-1">
            <Button size="sm" variant="ghost" onClick={() => { setComment(""); setCommentOpen(false); }}
                     className="h-8 text-xs rounded-none">Cancel</Button>
            <Button size="sm"
                     onClick={() => { if (comment.trim()) { onAction("comment", comment); setComment(""); setCommentOpen(false); } }}
                     disabled={!comment.trim()}
                     className="h-8 text-xs rounded-none"
                     data-testid={`cs-public-comment-send-${row.id}`}>
              Send
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
